// P3-018: Phaser game object for a single tile on the 7x5 board
// Uses gem sprite images for visuals

import Phaser from 'phaser';
import { ElementType } from '@/shared/types/common';
import { TILE_SIZE } from '../data/board-constants';

/** Element color mapping (used for particles/effects) */
const ELEMENT_COLORS: Record<ElementType, number> = {
  [ElementType.RED]: 0xe05545,
  [ElementType.BLUE]: 0x5890e0,
  [ElementType.GREEN]: 0x52c07a,
  [ElementType.YELLOW]: 0xe6b832,
  [ElementType.PURPLE]: 0x9b6ad8,
};

/** Element → Phaser texture key */
const ELEMENT_TEXTURE: Record<ElementType, string> = {
  [ElementType.RED]: 'gem-red',
  [ElementType.BLUE]: 'gem-blue',
  [ElementType.GREEN]: 'gem-green',
  [ElementType.YELLOW]: 'gem-yellow',
  [ElementType.PURPLE]: 'gem-purple',
};

const TILE_PADDING = 4;
const TILE_INNER = TILE_SIZE - TILE_PADDING * 2;
const CORNER_RADIUS = 6;

export class TileSprite extends Phaser.GameObjects.Container {
  /** Board column index (0-6) */
  public tileX: number;
  /** Board row index (0-4) */
  public tileY: number;
  public element: ElementType;

  private gemImage: Phaser.GameObjects.Image;
  private selectHighlight: Phaser.GameObjects.Graphics;
  private lockOverlay: Phaser.GameObjects.Graphics;
  private isSelected = false;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    element: ElementType,
    tileX: number,
    tileY: number,
  ) {
    super(scene, x, y);

    this.element = element;
    this.tileX = tileX;
    this.tileY = tileY;

    // Gem sprite image
    const textureKey = ELEMENT_TEXTURE[element] ?? 'gem-red';
    this.gemImage = scene.add.image(0, 0, textureKey);
    this.gemImage.setDisplaySize(TILE_INNER, TILE_INNER);
    this.add(this.gemImage);

    // Selection highlight (initially invisible)
    this.selectHighlight = scene.add.graphics();
    this.selectHighlight.setVisible(false);
    this.drawSelectHighlight();
    this.add(this.selectHighlight);

    // Lock overlay (initially invisible)
    this.lockOverlay = scene.add.graphics();
    this.lockOverlay.setVisible(false);
    this.drawLockOverlay();
    this.add(this.lockOverlay);

    // Make interactive
    this.setSize(TILE_SIZE, TILE_SIZE);
    this.setInteractive({ useHandCursor: true });

    scene.add.existing(this);
  }

  /** Draw selection highlight ring */
  private drawSelectHighlight(): void {
    this.selectHighlight.clear();
    this.selectHighlight.lineStyle(3, 0xffffff, 1);
    this.selectHighlight.strokeRoundedRect(
      -TILE_SIZE / 2 + 1,
      -TILE_SIZE / 2 + 1,
      TILE_SIZE - 2,
      TILE_SIZE - 2,
      CORNER_RADIUS + 2,
    );
  }

  /** Draw the locked tile overlay */
  private drawLockOverlay(): void {
    this.lockOverlay.clear();
    // Semi-transparent dark overlay
    this.lockOverlay.fillStyle(0x000000, 0.5);
    this.lockOverlay.fillRoundedRect(
      -TILE_INNER / 2,
      -TILE_INNER / 2,
      TILE_INNER,
      TILE_INNER,
      CORNER_RADIUS,
    );
    // Lock icon (simple cross-hatch lines)
    this.lockOverlay.lineStyle(2, 0xffffff, 0.4);
    const half = TILE_INNER / 2 - 4;
    for (let i = -half; i <= half; i += 10) {
      this.lockOverlay.lineBetween(i, -half, i + half, half);
    }
  }

  /** Set selected state with highlight and scale */
  setSelected(selected: boolean): void {
    if (this.isSelected === selected) return;
    this.isSelected = selected;
    this.selectHighlight.setVisible(selected);

    if (selected) {
      this.scene.tweens.add({
        targets: this,
        scaleX: 1.15,
        scaleY: 1.15,
        duration: 120,
        ease: 'Back.easeOut',
      });
    } else {
      this.scene.tweens.add({
        targets: this,
        scaleX: 1,
        scaleY: 1,
        duration: 100,
        ease: 'Sine.easeOut',
      });
    }
  }

  /** Set locked state visual */
  setLocked(locked: boolean): void {
    this.lockOverlay.setVisible(locked);
  }

  /** Animate swap to a target position */
  animateSwap(targetX: number, targetY: number): Promise<void> {
    return new Promise((resolve) => {
      this.scene.tweens.add({
        targets: this,
        x: targetX,
        y: targetY,
        duration: 200,
        ease: 'Sine.easeInOut',
        onComplete: () => {
          resolve();
        },
      });
    });
  }

  /** Animate tile falling from above */
  animateFall(targetY: number, delay = 0): Promise<void> {
    return new Promise((resolve) => {
      this.scene.tweens.add({
        targets: this,
        y: targetY,
        delay,
        duration: 250,
        ease: 'Bounce.easeOut',
        onComplete: () => {
          resolve();
        },
      });
    });
  }

  /** Animate tile rising from below (floating up toward enemy) */
  animateRise(targetY: number, delay = 0): Promise<void> {
    return new Promise((resolve) => {
      this.scene.tweens.add({
        targets: this,
        y: targetY,
        delay,
        duration: 280,
        ease: 'Back.easeOut',
        onComplete: () => {
          resolve();
        },
      });
    });
  }

  /** Play destroy animation (scale down + fade out) and remove from scene */
  animateDestroy(): Promise<void> {
    return new Promise((resolve) => {
      this.scene.tweens.add({
        targets: this,
        scaleX: 0,
        scaleY: 0,
        alpha: 0,
        duration: 250,
        ease: 'Back.easeIn',
        onComplete: () => {
          this.destroy();
          resolve();
        },
      });
    });
  }

  /** Show/hide match highlight ring (golden border, distinct from white selection) */
  setMatchHighlighted(highlighted: boolean): void {
    if (highlighted) {
      this.selectHighlight.clear();
      this.selectHighlight.lineStyle(3, 0xffdd00, 1.0);
      this.selectHighlight.strokeRoundedRect(
        -TILE_SIZE / 2 + 1,
        -TILE_SIZE / 2 + 1,
        TILE_SIZE - 2,
        TILE_SIZE - 2,
        CORNER_RADIUS + 2,
      );
      this.selectHighlight.setVisible(true);
    } else {
      this.selectHighlight.setVisible(false);
      // Restore normal selection highlight for future use
      this.drawSelectHighlight();
    }
  }

  /** Update the element type (for board changes) */
  setElement(newElement: ElementType): void {
    this.element = newElement;
    const textureKey = ELEMENT_TEXTURE[newElement] ?? 'gem-red';
    this.gemImage.setTexture(textureKey);
  }

  /** Get the color for this tile's element */
  getColor(): number {
    return ELEMENT_COLORS[this.element] ?? 0xffffff;
  }
}
